import React, { useState, Fragment, useEffect, useReducer } from 'react';
import styled from 'styled-components';
import { filter, find, findIndex } from 'ramda';
import Modal from '~~elements/Modal';
import Button from '~~elements/Button';
import Loading from '~~elements/Loading';
import Input from '~~elements/Input';
import toast from '~~elements/Toast';
import Multilingual from '~~utils/Multilingual';
import { LeagueResource, SeasonResource, PlayerResource } from '~~apis/resource';
import { playerReducer, playerInitialState } from '../service';
import { StyledPlayer } from '../styled';
import { useTournamentDispatch, useTournamentState } from '../useTournament';
import PlayerDisplay from '../TournamentTeam/PlayerDisplay';
import PlayerEdit from '../TournamentTeam/PlayerEdit';
import PlayerNew from '../TournamentTeam/PlayerNew';
import PlayerDelete from '../TournamentTeam/PlayerDelete';
import { useRootState } from '../../../useRoot';
import moment from 'moment';
import json2csv from 'json-2-csv';
import XLSX from 'xlsx';

const rosterPositions = ['P', 'C', '1B', '2B', '3B', 'SS', 'LF', 'CF', 'RF', 'DH'];
const rosterHands = { R: 'R', L: 'L', BOTH: 'S' };
const rosterStatuses = { ACTIVE: 'active' };

// 轉成試算表的日期序號，避免時區造成日期偏移
const toExcelDate = (date) => {
  const m = moment(date);
  if (!date || !m.isValid()) {
    return { t: 's', v: '' };
  }

  return { t: 'n', v: (Date.UTC(m.year(), m.month(), m.date()) - Date.UTC(1899, 11, 30)) / 86400000, z: 'yyyy/m/d' };
};

const StyledDiv = styled.div`
  width: 100%;
  > h4 {
    border-bottom: 1px solid #eee;
  }
`;

const teamTitle = (team) => {
  const title = `${team.origin.city} ${team.name}`;
  if (team.name === team.origin.name) {
    return title;
  }

  return `${title}（${team.origin.name}）`;
};

const TeamModal = ({ selectedTeam, modalTeam, handleCancelTeamSelect, teamUpdated }) => {
  const { permissionList } = useRootState();
  const { organizerUniqid, tournamentInfo, games, teams } = useTournamentState();
  const tournamentDispatch = useTournamentDispatch();

  const [preparing, setPreparing] = useState(false);
  const [teamPlayers, setTeamPlayers] = useState([]);

  const [isEditMode, setIsEditMode] = useState(false);

  const [inSchedule, setInSchedule] = useState(false);
  const [tryUnregister, setTryUnregister] = useState(false);
  const [teamUnregisterInput, setTeamUnregisterInput] = useState('');

  const [tryChangeTeamName, setTryChangeTeamName] = useState(false);
  const [teamCityInput, setTeamCityInput] = useState('');
  const [teamNameInput, setTeamNameInput] = useState('');
  const [teamInControl, setTeamInControl] = useState(false);

  const [playerState, playerDispatch] = useReducer(playerReducer, playerInitialState);

  const [apiInProgress, setApiInProgress] = useState(false);

  useEffect(() => {
    setTeamPlayers([]);
    setIsEditMode(false);
    playerDispatch({ type: 'reset' });
    if (!tournamentInfo.uniqid || !modalTeam.uniqid) {
      return;
    }

    PlayerResource.getPlayersInSeasonTeam({
      seasonUniqid: tournamentInfo.uniqid,
      seasonTeamUniqid: modalTeam.uniqid,
    })
      .then(({ data }) => {
        setTeamPlayers(data);
        setPreparing(false);
      })
      .catch(() => {
        toast(Multilingual(`COMMON.UNKNOWN_ERROR`), { status: 'error' });
      });
  }, [modalTeam.uniqid, tournamentInfo.uniqid]);

  useEffect(() => {
    setInSchedule(false);
    setTryUnregister(false);
    setTeamUnregisterInput('');
    setTryChangeTeamName(false);
    setTeamCityInput(modalTeam.origin.city);
    setTeamNameInput(modalTeam.name);
    setApiInProgress(false);
    if (!modalTeam.name) {
      return;
    }

    if (!find((g) => g.away.team === modalTeam.name || g.home.team === modalTeam.name, games)) {
      return;
    }

    setInSchedule(true);
  }, [games, modalTeam]);

  useEffect(() => {
    setTeamInControl(false);
    if (!permissionList) {
      return;
    }

    for (let permissionTeam of permissionList.teams) {
      const teamUniqid = permissionTeam.info.uniqid;
      if (teamUniqid !== modalTeam.origin.uniqid) {
        continue;
      }

      setTeamInControl(true);
      return;
    }
  }, [modalTeam.origin.uniqid, permissionList]);

  const controlEditMode = (toOpen) => () => {
    setIsEditMode(toOpen);
  };

  const handleTeamUnregisterInput = (e) => {
    setTeamUnregisterInput(e.target.value);
  };

  const cancelTeamUnregister = () => {
    setTryUnregister(false);
  };

  const handleTeamUnregister = () => {
    if (!tryUnregister) {
      setTryUnregister(true);
      return;
    }

    if (teamUnregisterInput !== modalTeam.name) {
      toast(Multilingual(`TEAM.UNREGISTER_HINT`), { status: 'error' });
      return;
    }

    setApiInProgress(true);
    SeasonResource.unregisterTournamentTeamByOrganizer({
      seasonUniqid: tournamentInfo.uniqid,
      seasonTeamUniqid: modalTeam.uniqid,
    })
      .then(() => {
        refetchTeams().then(() => {
          toast(Multilingual(`TEAM.UNREGISTER_SUCCESS`), { status: 'success', second: 2 });
          handleCancelTeamSelect();
        });
      })
      .catch((e) => {
        console.error(e);
        let message = Multilingual(`COMMON.UNKNOWN_ERROR`);
        if (e.message === 'ALREADY_JOINED_GAMES') {
          message = Multilingual(`TOURNAMENT.ERROR.TEAM_ALREADY_JOINED_GAMES`);
        }
        toast(message, { status: 'error' });
        setApiInProgress(false);
      });
  };

  const handleTeamCityInput = (e) => {
    setTeamCityInput(e.target.value);
  };

  const handleTeamNameInput = (e) => {
    setTeamNameInput(e.target.value);
  };

  const cancelTeamNameChange = () => {
    setTryChangeTeamName(false);
    setTeamCityInput(modalTeam.origin.city);
    setTeamNameInput(modalTeam.name);
  };

  const handleTeamNameChange = () => {
    if (!tryChangeTeamName) {
      setTryChangeTeamName(true);
      return;
    }

    const newTeamName = teamNameInput.trim();
    if (!newTeamName) {
      toast(`${Multilingual(`TEAM.NAME`)} ${Multilingual(`FORM.ERROR.IS_REQUIRED`)}`, { status: 'error' });
      return;
    }

    if (newTeamName === modalTeam.name && teamCityInput.trim() === modalTeam.origin.city) {
      setTryChangeTeamName(false);
      return;
    }

    if (find((t) => t.name === newTeamName, teams) && newTeamName !== modalTeam.name) {
      toast(`${Multilingual(`TEAM.NAME`)} ${Multilingual(`FORM.ERROR.DUPLICATE`)}`, { status: 'error' });
      return;
    }

    setApiInProgress(true);
    SeasonResource.updateTournamentTeamByOrganizer({
      seasonUniqid: tournamentInfo.uniqid,
      seasonTeamUniqid: modalTeam.uniqid,
      data: { name: newTeamName, city: teamCityInput.trim() },
    })
      .then(({ data: newTeam }) => {
        refetchTeams().then(() => {
          toast(Multilingual(`SUCCESS.UPDATE`), { status: 'success', second: 2 });
          teamUpdated(newTeam);
        });
      })
      .catch((e) => {
        console.error(e);
        toast(Multilingual(`COMMON.UNKNOWN_ERROR`), { status: 'error' });
        setApiInProgress(false);
      });
  };

  const refetchTeams = () => {
    return new Promise((resolve) => {
      SeasonResource.getTeams({ seasonUniqid: tournamentInfo.uniqid })
        .then(({ data: newTeams }) => {
          tournamentDispatch({ type: 'SET_TEAMS', payload: newTeams });
          resolve();
        })
        .catch(() => {
          window.location.reload();
        });
    });
  };

  const refetchPlayers = () => {
    setTeamPlayers([]);
    playerDispatch({ type: 'reset' });
    PlayerResource.getPlayersInSeasonTeam({
      seasonUniqid: tournamentInfo.uniqid,
      seasonTeamUniqid: modalTeam.uniqid,
    })
      .then(({ data }) => {
        setTeamPlayers(data);
      })
      .catch(() => {
        toast(Multilingual(`COMMON.UNKNOWN_ERROR`), { status: 'error' });
      });
  };

  // 往回抓五個賽事，有球隊參與時停下來
  const getPreSeasonPlayers = () => {
    return new Promise((resolve, reject) => {
      LeagueResource.getLeagueWithSeasons({ leagueUniqid: organizerUniqid })
        .then(({ data: { seasons: tournaments } }) => {
          const tournamentAmount = tournaments.length;
          const currentTournamentIndex = findIndex((s) => s.uniqid === tournamentInfo.uniqid, tournaments);
          if (tournamentAmount === 1 || currentTournamentIndex === tournamentAmount - 1) {
            resolve([]);
          }

          const preTournaments = tournaments.slice(currentTournamentIndex + 1);
          let preTournamentIndex = 0;
          const getMatched = () => {
            const preTournament = preTournaments[preTournamentIndex];
            if (!preTournament || preTournamentIndex >= 5) {
              resolve([]);
              return;
            }

            SeasonResource.getTeams({ seasonUniqid: preTournament.uniqid })
              .then(({ data: preTournamentTeams }) => {
                // 避免球隊之前的賽事沒有參與造成註冊困難
                const matchedTeams = filter((t) => t.origin.uniqid === modalTeam.origin.uniqid, preTournamentTeams);
                if (matchedTeams.length === 0) {
                  preTournamentIndex++;
                  getMatched();
                  return;
                }

                let preTournamentTeamCount = matchedTeams.length;
                const preTournamentPlayers = [];
                const playersProcess = (players) => {
                  preTournamentTeamCount--;
                  players.forEach((player) => {
                    const exist = find(
                      (p) => p.name === player.name && p.number === player.number,
                      preTournamentPlayers,
                    );
                    if (exist) {
                      return;
                    }

                    preTournamentPlayers.push(player);
                  });
                  if (preTournamentTeamCount > 0) {
                    return;
                  }

                  resolve(preTournamentPlayers);
                };

                matchedTeams.forEach((matchedTeam) => {
                  PlayerResource.getPlayersInSeasonTeam({
                    seasonUniqid: preTournament.uniqid,
                    seasonTeamUniqid: matchedTeam.uniqid,
                  })
                    .then(({ data }) => {
                      playersProcess(data);
                    })
                    .catch((e) => {
                      reject(e);
                    });
                });
              })
              .catch((e) => {
                reject(e);
              });
          };
          getMatched();
        })
        .catch((e) => {
          reject(e);
        });
    });
  };

  const handleNewPlayer = () => {
    playerDispatch({ type: 'start', payload: { action: 'create' } });
  };

  const handlePlayerEdit = (index) => () => {
    const targetPlayer = teamPlayers[index];
    const birthday = new Date(targetPlayer.birthday);
    let birthdayInput = '';
    if (targetPlayer.birthday && moment(birthday).isValid()) {
      birthdayInput = moment(birthday).format('YYYY-MM-DD');
    }

    playerDispatch({
      type: 'start',
      payload: {
        action: 'edit',
        ...teamPlayers[index],
        newName: teamPlayers[index].name,
        newNumber: teamPlayers[index].number,
        birthday: birthdayInput,
      },
    });
  };

  const downloadPlayerCsv = async () => {
    const players = teamPlayers.map((player) => ({
      name: player.name,
      number: player.number,
      birthday: player.birthday,
      pHand: player.p_hand,
      bHand: player.b_hand,
      position: player.first_position,
    }));
    json2csv.json2csv(players, (err, csvContent) => {
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${modalTeam.name}-players.csv`;
      a.click();
      URL.revokeObjectURL(url);
    });
  };

  // 欄位結構同秩序冊匯入範本；平台沒有名冊類型先帶預設值，生效日期為賽事開始日前 10 天
  const downloadPlayerRoster = () => {
    const header = ['姓名', '背號', '守位', '生日', '身高', '體重', '投', '打', '名冊類型', '名冊狀態', '生效日期'];
    const effectiveDate = toExcelDate(
      tournamentInfo.start_at && moment(tournamentInfo.start_at).subtract(10, 'days').format('YYYY-MM-DD'),
    );
    const rows = teamPlayers.map((player) => [
      player.name,
      { t: 's', v: `${player.number}`, z: '@' },
      rosterPositions.includes(player.first_position) ? player.first_position : '',
      toExcelDate(player.birthday),
      '',
      '',
      rosterHands[player.p_hand] || '',
      rosterHands[player.b_hand] || '',
      'ACTIVE_25',
      rosterStatuses[player.roster_status] || '',
      effectiveDate,
    ]);

    const sheet = XLSX.utils.aoa_to_sheet([header, ...rows]);
    sheet['!cols'] = header.map(() => ({ wch: 14 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, sheet, '秩序冊');
    XLSX.writeFile(wb, `${modalTeam.name}-roster.xlsx`);
  };

  const handlePlayerDelete = (index) => () => {
    playerDispatch({ type: 'start', payload: { action: 'delete', ...teamPlayers[index] } });
  };

  const getHeader = () => {
    if (tryChangeTeamName) {
      return (
        <span>
          <Button
            width="unset"
            color="transparentWithBorder"
            mini
            onClick={cancelTeamNameChange}
            disabled={apiInProgress}
          >
            {Multilingual(`COMMON.CANCEL`)}
          </Button>
          <Button width="unset" color="red" mini onClick={handleTeamNameChange} disabled={apiInProgress}>
            {Multilingual(`COMMON.UPDATE`)}
          </Button>
          <Input
            name="city"
            placeholder={Multilingual(`TEAM.CITY`)}
            value={teamCityInput}
            onChange={handleTeamCityInput}
            disabled={apiInProgress || !teamInControl}
            {...(teamInControl ? {} : { title: Multilingual(`TEAM.CITY_DENY_IN_TOURN_HINT`) })}
          />
          <Input
            name="name"
            placeholder={Multilingual(`TEAM.NAME`)}
            value={teamNameInput}
            onChange={handleTeamNameInput}
            disabled={apiInProgress}
          />
        </span>
      );
    }

    if (isEditMode) {
      return (
        <span>
          <Button width="unset" color="red" mini onClick={handleTeamNameChange}>
            {Multilingual(`COMMON.EDIT`)}
          </Button>
          {teamTitle(modalTeam)}
        </span>
      );
    }

    return <Fragment>{teamTitle(modalTeam)}</Fragment>;
  };

  const getPlayerCard = (player, index) => {
    const defaultCard = (
      <PlayerDisplay
        editable={isEditMode}
        player={player}
        handlePlayerEdit={handlePlayerEdit(index)}
        handlePlayerDelete={handlePlayerDelete(index)}
      />
    );

    if (playerState.name !== player.name || playerState.number !== player.number) {
      return defaultCard;
    }

    switch (playerState.action) {
      case 'delete':
        return (
          <Fragment>
            <PlayerDelete
              playerState={playerState}
              playerDispatch={playerDispatch}
              seasonUniqid={tournamentInfo.uniqid}
              seasonTeamUniqid={modalTeam.uniqid}
              done={refetchPlayers}
            />
            {defaultCard}
          </Fragment>
        );
      case 'edit':
        return (
          <PlayerEdit
            playerState={playerState}
            playerDispatch={playerDispatch}
            players={teamPlayers}
            seasonUniqid={tournamentInfo.uniqid}
            seasonTeamUniqid={modalTeam.uniqid}
            done={refetchPlayers}
          />
        );
      default:
        return defaultCard;
    }
  };

  const getFooter = () => {
    if (tryUnregister) {
      return (
        <div style={{ width: '100%', overflowX: 'auto', textAlign: 'center' }}>
          <label style={{ fontSize: '0.8rem' }}>
            {Multilingual(`TEAM.UNREGISTER_HINT`)}
            <Input
              width="5rem"
              placeholder={modalTeam.name}
              value={teamUnregisterInput}
              onChange={handleTeamUnregisterInput}
            />
          </label>
          <Button width="unset" nowrap color="red" onClick={handleTeamUnregister} disabled={apiInProgress}>
            {Multilingual(`TEAM.UNREGISTER_CONFIRM`)}
          </Button>
          <Button
            width="unset"
            nowrap
            color="transparentWithBorder"
            onClick={cancelTeamUnregister}
            disabled={apiInProgress}
          >
            {Multilingual(`COMMON.CANCEL`)}
          </Button>
        </div>
      );
    }

    if (isEditMode) {
      return (
        <Fragment>
          <Button width="unset" color="transparentWithBorder" nowrap onClick={controlEditMode(false)}>
            {Multilingual(`COMMON.BACK`)}
          </Button>
          {!inSchedule && (
            <Button width="unset" mini nowrap color="transparent" onClick={handleTeamUnregister}>
              {`${Multilingual(`TEAM.WORDING`)} ${Multilingual(`TEAM.UNREGISTER`)}`}
            </Button>
          )}
        </Fragment>
      );
    }

    return (
      <Fragment>
        <Button width="unset" nowrap color="red" onClick={controlEditMode(true)}>
          {Multilingual(`FORM.EDIT_MODE`)}
        </Button>
        <a
          href={`${process.env.REACT_APP_MAINPAGE_URL}/team/${modalTeam.origin.uniqid}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          <Button width="unset" color="blue" nowrap>
            {Multilingual(`COMMON.VIEW`)}
          </Button>
        </a>
        <Button width="unset" nowrap color="transparentWithBorder" onClick={downloadPlayerCsv}>
          CSV
        </Button>
        <Button width="unset" nowrap color="transparentWithBorder" onClick={downloadPlayerRoster}>
          Excel
        </Button>
      </Fragment>
    );
  };

  return (
    <Modal.Modal
      size="lg"
      isOpen={!!selectedTeam}
      outterClickBack={!apiInProgress}
      onClickBack={handleCancelTeamSelect}
    >
      <Modal.ModalHeader hasBack={!apiInProgress} onClickBack={handleCancelTeamSelect}>
        {getHeader()}
      </Modal.ModalHeader>
      <Modal.ModalBody>
        {preparing ? (
          <Loading w="5rem" />
        ) : (
          <StyledDiv>
            <h4>
              {Multilingual(`TEAM.PLAYER`)}
              {isEditMode && (
                <Button width="unset" nowrap color="red" mini onClick={handleNewPlayer}>
                  {Multilingual(`PLAYER.REGISTER`)}
                </Button>
              )}
            </h4>
            <StyledPlayer>
              <div className="registration--list">
                {playerState.action === 'create' && (
                  <div className="registration--item">
                    <PlayerNew
                      playerState={playerState}
                      playerDispatch={playerDispatch}
                      players={teamPlayers}
                      getPreSeasonPlayers={getPreSeasonPlayers}
                      seasonName={tournamentInfo.title}
                      seasonUniqid={tournamentInfo.uniqid}
                      teamName={modalTeam.name}
                      seasonTeamUniqid={modalTeam.uniqid}
                      done={refetchPlayers}
                    />
                  </div>
                )}
                {teamPlayers.map((player, index) => (
                  <div className="registration--item" key={player.uniqid}>
                    {getPlayerCard(player, index)}
                  </div>
                ))}
              </div>
            </StyledPlayer>
          </StyledDiv>
        )}
      </Modal.ModalBody>
      <Modal.ModalFooter>{getFooter()}</Modal.ModalFooter>
    </Modal.Modal>
  );
};

export default TeamModal;
